import Cookies from 'js-cookie'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { RefObject, createRef, useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import { Provider } from 'react-redux'
import { Context as ResponsiveContext } from 'react-responsive'
import { v4 as uuidv4 } from 'uuid'
import { setLocale as setYupLocale } from 'yup'

import Bugsnag from '@bugsnag/js'
import { DefaultError } from 'errors/components/DefaultError'
import { GenericErrorBoundary } from 'errors/components/GenericErrorBoundary'
import ExperimentProvider from 'experiments/providers/ExperimentProvider'
import { HyperLinks } from 'head'
import TrackingScripts from 'head/components/TrackingScripts'
import { LANGUAGE_COOKIE_NAME } from 'localization'
import { getReferrerInfo, getUtmQueryParams } from 'mParticle/mParticleService'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { AppProps } from 'next/app'
import 'normalize.css'
import ShoppingRegionContextProvider from 'shared/contexts/ShoppingRegionContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { STORAGE } from 'shared/enums/SitePreferences'
import withLocalization from 'shared/hocs/withLocalization'
import withTracking from 'shared/hocs/withTracking'
import PageLayout from 'shared/layouts/PageLayout'
import { startBugsnag } from 'shared/lib/bugsnag'
import 'shared/polyfills.js'
import 'shared/styles/global.css'
import { getClientAnonymousUserId, getClientDeviceId } from 'shared/utils/clientAnalyticsIds'
import { getSecureCookieFlag } from 'shared/utils/getSecureCookieFlag'
import { GetServerPageProps } from 'shared/utils/getServerSideProps'
import { requestService } from 'shared/utils/requestService'
import { setFeatureFlags } from 'store/featureFlagsSlice'
import { getStoreInstance } from 'store/store'

type FCAppProps = {
  appContainerRef: RefObject<HTMLDivElement>
  avantSiteID: string
  isDesktop: boolean
}

if (!Bugsnag.isStarted()) {
  startBugsnag()
}

const ErrorBoundary = Bugsnag.isStarted()
  ? Bugsnag.getPlugin('react')!.createErrorBoundary(React)
  : GenericErrorBoundary

const FlightClubApp = ({
  avantSiteID,
  Component,
  pageProps,
}: AppProps<GetServerPageProps> & FCAppProps) => {
  const {
    availableCurrencies,
    canonicalUrl,
    cmsBanner,
    collectionsDirectory,
    currentPath,
    isAuthenticated,
    featureFlags: initialFeatureFlags,
    footerData,
    locale,
    shoppingRegionData,
    initWidth,
  } = pageProps
  const appContainerRef = createRef<HTMLDivElement>()
  const [clientDedupID] = useState(uuidv4())
  const [hasLoggedUtmEvent, setHasLoggedUtmEvent] = useState(false)
  const [width, setWidth] = useState(initWidth)
  const handleResize = () => setWidth(window.innerWidth)

  // Initialize store with FF for smooth initial hydration.
  const store = getStoreInstance({ features: { featureFlags: initialFeatureFlags } })

  // Bootstrap the application with initial cookies
  useEffect(() => {
    /**
     * Check if essential middlewares have run:
     * - csrfProtection? -> checks for CSRF token which enables POST/PUT requests
     * - regionPreferenceMiddleware? -> checks for related cookies
     *
     * If not this means the client has loaded a cached page and we need to make a request
     * to the server to get the necessary cookies.
     */
    const startSession = async () => {
      if (
        !Cookies.get('_csrf') ||
        !Cookies.get(STORAGE.CURRENCY) ||
        !Cookies.get(STORAGE.CURRENCYCODE)
      ) {
        await requestService({ url: '/session', withCredentials: true })
      }
    }

    startSession()
    getClientAnonymousUserId()
    getClientDeviceId()
  }, [])

  // log utm event
  useEffect(() => {
    if (!hasLoggedUtmEvent) {
      const utmQueryParams = getUtmQueryParams()
      sendTrackingEvent('CAMPAIGN_SESSION_START', {
        ...getReferrerInfo(),
        ...utmQueryParams,
        client_dedup_id: clientDedupID,
      })
      setHasLoggedUtmEvent(true)
    }
  }, [hasLoggedUtmEvent])

  // set locale in cookie
  useEffect(() => {
    Cookies.set(LANGUAGE_COOKIE_NAME, locale, { path: '/', secure: getSecureCookieFlag() })
  }, [locale])

  useEffect(() => {
    // set yup locale
    setYupLocale({
      mixed: {
        required: '${path} is a required field.',
      },
    })

    // hide app from assistive screenreaders and other assistive technologies while the modal is open.
    if (appContainerRef.current) {
      ReactModal.setAppElement(appContainerRef.current)
    }
  }, [])

  // rehydrate store feature flags with newer values as different pages load
  useEffect(() => {
    store.dispatch(setFeatureFlags(initialFeatureFlags))
  }, [initialFeatureFlags?.ts])

  // set window width on resize and on mount
  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize) // cleanup
    }
  }, [])

  const { pathname } = useRouter()
  const isEditorialOrArticlePage =
    pathname?.includes('/editorial') || pathname?.includes('/article')
  const areHreflangEnabled = initialFeatureFlags?.[FeatureFlag.TEMP_WEB_FC_HREFLANG] ?? false
  const baseUrl = 'https://www.flightclub.com'
  const path = (() => {
    if (currentPath === '/') {
      return ''
    }
    if (currentPath?.includes('?')) {
      return currentPath.split('?')[0]
    }
    return currentPath
  })()
  const enUSUrl = `${baseUrl}${path}`
  const jaJPUrl = `${baseUrl}/ja-jp${path}`
  const enJPUrl = `${baseUrl}/en-jp${path}`

  return (
    <ErrorBoundary
      FallbackComponent={DefaultError}
      onError={(e) => {
        Bugsnag.leaveBreadcrumb('BugsnagProviderErrorBoundary', {
          message: e.errors[0]?.errorMessage,
          fallback: 'DefaultError',
        })
      }}
    >
      <div ref={appContainerRef}>
        <Head>
          <title>Sneakers. Here. | Flight Club</title>
          {areHreflangEnabled && (
            <>
              <link rel="alternate" hrefLang="x-default" href={enUSUrl} />
              <link rel="alternate" hrefLang="en-us" href={enUSUrl} />
              {!isEditorialOrArticlePage && (
                <link rel="alternate" hrefLang="ja-jp" href={jaJPUrl} />
              )}
              {!isEditorialOrArticlePage && (
                <link rel="alternate" hrefLang="en-jp" href={enJPUrl} />
              )}
            </>
          )}
        </Head>
        <Provider store={store}>
          <ResponsiveContext.Provider value={{ width }}>
            <TrackingScripts avantSiteID={avantSiteID} />
            <ShoppingRegionContextProvider
              shoppingRegionData={shoppingRegionData}
              availableCurrencies={availableCurrencies}
              clientSideLocale={locale}
            >
              <ExperimentProvider>
                <HyperLinks canonicalUrl={canonicalUrl} currentPath={currentPath} />
                <PageLayout
                  isAuthenticated={isAuthenticated}
                  footerData={footerData}
                  cmsBanner={cmsBanner}
                  collectionsDirectory={collectionsDirectory}
                >
                  <Component {...pageProps} />
                </PageLayout>
              </ExperimentProvider>
            </ShoppingRegionContextProvider>
          </ResponsiveContext.Provider>
        </Provider>
      </div>
    </ErrorBoundary>
  )
}

// prettier-ignore
export default
  withTracking(
    withLocalization(FlightClubApp)
  )
